import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from 'recharts';
import { CampaignManagementService } from '../../../services/campaignManagementService';

const ABTestingWidget = ({ abTests, isLoading, className = '' }) => {
  const [selectedTest, setSelectedTest] = useState(null);
  const [abTestData, setAbTestData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load A/B testing data from Supabase
  useEffect(() => {
    loadABTestData();
  }, []);

  const loadABTestData = async () => {
    setLoading(true);
    try {
      const { data, error: abError } = await CampaignManagementService?.getABTestingData();
      
      if (abError) {
        setError(abError);
        setAbTestData([]);
      } else {
        setAbTestData(data || []);
        // Set first test as selected if available
        if (data?.length > 0 && !selectedTest) {
          setSelectedTest(data?.[0]?.id);
        }
      }
    } catch (error) {
      setError('Failed to load A/B testing data');
      setAbTestData([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'completed': return 'text-primary bg-primary/10';
      case 'draft': return 'text-muted-foreground bg-muted';
      case 'paused': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'Play';
      case 'completed': return 'CheckCircle';
      case 'draft': return 'FileText';
      case 'paused': return 'Pause';
      default: return 'Circle';
    }
  };

  const getSignificanceColor = (confidence) => {
    if (confidence >= 90) return 'text-success';
    if (confidence >= 70) return 'text-warning';
    if (confidence >= 50) return 'text-error';
    return 'text-muted-foreground';
  };

  const currentTest = abTestData?.find(test => test?.id === selectedTest);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    })?.format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US')?.format(value);
  };

  const formatPercentage = (value) => {
    return `${value?.toFixed(2)}%`;
  };

  // Generate pie chart data from current test variants
  const pieData = currentTest?.variants?.map((variant, index) => ({
    name: variant?.name,
    value: variant?.scans || 0,
    fill: index === 0 ? 'var(--color-primary)' : 'var(--color-secondary)'
  })) || [];

  // Generate bar chart data from current test variants
  const barData = currentTest?.variants?.map(variant => ({
    name: variant?.name?.split(' ')?.[0] || 'Variant',
    conversions: variant?.conversions || 0,
    scans: variant?.scans || 0
  })) || [];

  if (isLoading || loading) {
    return (
      <div className={`bg-card border border-border rounded-lg ${className}`}>
        <div className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading A/B tests...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="FlaskConical" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">A/B Testing Results</h3>
          </div>
          <Button variant="ghost" size="icon">
            <Icon name="Settings" size={16} />
          </Button>
        </div>

        {/* Test Selector */}
        {abTestData?.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {abTestData?.map((test) => (
              <Button
                key={test?.id}
                variant={selectedTest === test?.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedTest(test?.id)}
                className="text-xs"
              >
                <div className={`w-2 h-2 rounded-full mr-2 ${getStatusColor(test?.status)?.split(' ')?.[1]}`}></div>
                {test?.name}
              </Button>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            No A/B tests available
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg m-6">
          <div className="flex items-center">
            <Icon name="AlertTriangle" size={20} className="text-yellow-600 mr-3" />
            <p className="text-yellow-800">{error}</p>
          </div>
        </div>
      )}

      {/* Test Details */}
      {currentTest ? (
        <div className="p-6">
          {/* Test Overview */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h4 className="font-medium text-foreground mb-1">{currentTest?.name}</h4>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(currentTest?.status)}`}>
                  <Icon name={getStatusIcon(currentTest?.status)} size={12} />
                  {currentTest?.status?.charAt(0)?.toUpperCase() + currentTest?.status?.slice(1)}
                </div>
                <span>
                  {currentTest?.totalVariants} variant{currentTest?.totalVariants > 1 ? 's' : ''}
                </span>
              </div>
            </div>
            
            {currentTest?.winner && (
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Winner</div>
                <div className="font-medium text-success">
                  {currentTest?.winner?.name}
                </div>
              </div>
            )}
          </div>

          {/* Confidence */}
          {currentTest?.isRunning && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-3 bg-muted/20 rounded-lg">
                <div className="text-2xl font-bold text-foreground">{currentTest?.confidence || 0}%</div>
                <div className="text-sm text-muted-foreground">Confidence</div>
              </div>
              <div className="text-center p-3 bg-muted/20 rounded-lg">
                <div className={`text-sm font-medium ${getSignificanceColor(currentTest?.confidence)}`}>
                  {currentTest?.confidence >= 90 ? 'HIGH' : currentTest?.confidence >= 70 ? 'MEDIUM' : 'LOW'}
                </div>
                <div className="text-sm text-muted-foreground">Significance</div>
              </div>
            </div>
          )}

          {/* Variants Comparison */}
          <div className="space-y-4 mb-6">
            <h5 className="font-medium text-foreground">Variant Performance</h5>
            {currentTest?.variants?.map((variant, index) => (
              <div 
                key={variant?.id} 
                className={`p-4 rounded-lg border ${
                  currentTest?.winner?.id === variant?.id 
                    ? 'border-success bg-success/5' :'border-border bg-muted/10'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-primary' : 'bg-secondary'}`}></div>
                    <span className="font-medium text-foreground">{variant?.name}</span>
                    {currentTest?.winner?.id === variant?.id && (
                      <Icon name="Crown" size={16} className="text-success" />
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {formatNumber(variant?.scans || 0)} scans
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Conversions</div>
                    <div className="font-medium text-foreground">{formatNumber(variant?.conversions || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Conv. Rate</div>
                    <div className="font-medium text-foreground">{formatPercentage(variant?.conversionRate || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Status</div>
                    <div className="font-medium text-foreground">
                      {variant?.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Charts */}
          {currentTest?.variants?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Traffic Distribution */}
              <div>
                <h6 className="font-medium text-foreground mb-3">Traffic Distribution</h6>
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={50}
                        dataKey="value"
                      >
                        {pieData?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry?.fill} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [formatNumber(value), 'Scans']}
                        contentStyle={{
                          backgroundColor: 'var(--color-popover)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '6px',
                          fontSize: '12px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Performance Comparison */}
              <div>
                <h6 className="font-medium text-foreground mb-3">Performance Comparison</h6>
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData}>
                      <XAxis 
                        dataKey="name" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }}
                      />
                      <YAxis hide />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'var(--color-popover)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '6px',
                          fontSize: '12px'
                        }}
                        formatter={(value, name) => [
                          formatNumber(value),
                          name === 'conversions' ? 'Conversions' : 'Scans'
                        ]}
                      />
                      <Bar dataKey="conversions" fill="var(--color-primary)" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Recommended Action */}
          <div className="p-4 bg-muted/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Lightbulb" size={16} className="text-warning" />
              <span className="font-medium text-foreground">Recommendation</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {currentTest?.winner && currentTest?.confidence >= 90 
                ? `Implement the winning variant (${currentTest?.winner?.name}) to improve performance.`
                : currentTest?.isRunning
                  ? 'Continue running the test to reach statistical significance.'
                  : 'Start the A/B test to begin collecting performance data.'}
            </div>
          </div>
        </div>
      ) : abTestData?.length === 0 ? (
        <div className="p-6">
          <div className="text-center py-8">
            <Icon name="FlaskConical" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="font-medium text-foreground mb-2">No A/B Tests</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Create campaigns with multiple QR codes to start A/B testing.
            </p>
            <Button onClick={() => window.location.href = '/qr-code-generator'}>
              Create QR Code
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ABTestingWidget;